/* ChangePasswordSheet — modal sheet for changing the lawyer's password.
   Uses PUT /auth/me which accepts an optional `password` field. */
import { useState } from "react";
import { createPortal } from "react-dom";
import { X, LockKey, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { userUpdateMe, formatUserApiError } from "../../lib/userApi";

export default function ChangePasswordSheet({ open, onClose }) {
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) {
      toast.error("كلمة المرور قصيرة جداً (٨ أحرف على الأقل)");
      return;
    }
    if (pwd !== confirm) {
      toast.error("كلمتا المرور غير متطابقتين");
      return;
    }
    setLoading(true);
    try {
      await userUpdateMe({ password: pwd });
      toast.success("تمّ تغيير كلمة المرور بنجاح");
      setPwd("");
      setConfirm("");
      onClose();
    } catch (err) {
      toast.error(formatUserApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-start justify-center pt-[env(safe-area-inset-top,0px)]" dir="rtl" data-testid="change-password-sheet">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-md bg-white rounded-b-[28px] p-6 mt-0 font-tajawal shadow-2xl"
        style={{ animation: "lex-sheet-in 280ms cubic-bezier(0.22,1,0.36,1)" }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 left-4 w-9 h-9 rounded-full bg-[#FAF9F6] flex items-center justify-center active:scale-95"
          aria-label="إغلاق"
          data-testid="change-password-close"
        >
          <X size={18} weight="bold" />
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-full bg-[#D4AF37]/15 flex items-center justify-center">
            <LockKey size={22} weight="duotone" color="#D4AF37" />
          </div>
          <div>
            <h2 className="text-[19px] font-bold text-[#0A192F]">تغيير كلمة المرور</h2>
            <p className="text-[13px] text-[#5A6470]">اختر كلمة مرور قويّة لحماية حسابك</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="text-[13px] text-[#5A6470] font-medium">كلمة المرور الجديدة</span>
            <input
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              data-testid="change-password-new"
              className="mt-1 w-full px-4 py-3 rounded-2xl border border-[#EAEAEA] bg-[#FAF9F6] focus:outline-none focus:border-[#D4AF37] text-[15px]"
              placeholder="٨ أحرف على الأقل"
              autoFocus
              required
            />
          </label>
          <label className="block">
            <span className="text-[13px] text-[#5A6470] font-medium">تأكيد كلمة المرور</span>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="change-password-confirm"
              className="mt-1 w-full px-4 py-3 rounded-2xl border border-[#EAEAEA] bg-[#FAF9F6] focus:outline-none focus:border-[#D4AF37] text-[15px]"
              required
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            data-testid="change-password-submit"
            className="w-full mt-2 bg-[#0A192F] text-white rounded-full py-3.5 text-[15px] font-bold flex items-center justify-center gap-2 active:scale-95 disabled:opacity-60"
          >
            <Check size={16} weight="bold" />
            {loading ? "جارٍ الحفظ..." : "حفظ كلمة المرور"}
          </button>
        </form>
      </div>
      <style>{`
        @keyframes lex-sheet-in {
          from { transform: translateY(20%); opacity: 0; }
          to   { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>,
    document.body
  );
}
